import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../../../translation/namespace';
import { ImageGallery } from '../../../../../types/api/ImageType';
import IForm from '../../../../../types/IForm';
import { SkipableContent } from '../../SkipableContent';
import { ImageList } from '../../ImageList';
import { GalleryHeadline } from '../../GalleryHeadline';

import { useSortableList } from '../../../../../_lib/components/SortableContentList/useSortableList';
import { SortableListContextProvider } from '../../../../../_lib/DragAndDrop';
import { DraggableImageListItem } from './DraggableImageListItem';
import { SonForm } from '../../../../../_lib/Form2';
import { AnyParameter } from '../../../../../_lib/Form2/types/SonForm';

export type Forms = {
  edit: IForm;
  delete: IForm;
  moveForward: IForm;
  moveBack: IForm;
};

export type EditableImage = {
  id: string;
  image: ImageGallery;
  forms?: Forms;
  metadataComplete: boolean;
  fetchUrl: string;
};

export type Image = {
  image: ImageGallery;
};

type EditableGalleryProps = {
  id: string;
  images: EditableImage[];
  setSyncedImages: Dispatch<SetStateAction<EditableImage[]>>;
  updateOrderForm: SonForm<AnyParameter>;
  headlineRef: React.RefObject<HTMLHeadingElement | null>;
  refetchImages: () => void;
};

export default function EditableGallery({
  id,
  images,
  setSyncedImages,
  updateOrderForm,
  headlineRef,
  refetchImages,
}: EditableGalleryProps) {
  const { t } = useTranslation(Namespace.ImageGallery);
  const { sortableItems, ...context } = useSortableList([images, setSyncedImages], updateOrderForm);

  /**
   * Necessary for empty image galleries
   * No images => no rendering.
   */
  if (!images || images.length === 0) {
    return null;
  }

  return (
    <>
      <GalleryHeadline id={id} ref={headlineRef}>
        {t('headlineUploadPreview')}
      </GalleryHeadline>
      <p className="image-gallery__upload-description font-regular-14-19">{t('uploadDescription')}</p>
      <SkipableContent id={id}>
        <SortableListContextProvider {...context}>
          <ImageList>
            {sortableItems.map(item => (
              <DraggableImageListItem key={item.id} {...item} refetchImages={refetchImages} />
            ))}
          </ImageList>
        </SortableListContextProvider>
      </SkipableContent>
    </>
  );
}
